import { htmlToPlainText } from './richText'

/**
 * What an email body may contain, and who a mail may be addressed to.
 *
 * <p>
 * Deliberately a second list beside the quotation document's. A narrative is drawn by
 * QuestPDF, so a tag it cannot draw is formatting the customer never sees - which is why
 * that list stops at what the toolbar writes. An email is drawn by a mail client, where a
 * signature's table, a background colour and a typeface all arrive, so this one is as wide
 * as the client is.
 * </p>
 *
 * The API sanitises the same markup on the way in. This copy is what keeps the editor
 * honest while somebody types; it is not the copy that decides.
 */

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN', 'HR',
  'H1', 'H2', 'H3', 'H4',
  'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'SUB', 'SUP',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'A',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD',

  // A browser that ignores styleWithCSS writes this instead of a styled span, and mail
  // clients honour it.
  'FONT',
])

/** Tags whose text is not body text, and goes with them. */
const DROPPED_WHOLE = new Set(['SCRIPT', 'STYLE', 'HEAD', 'TITLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM'])

const ALLOWED_ATTRIBUTES = new Set([
  'style', 'href', 'title', 'target', 'rel',
  'align', 'colspan', 'rowspan', 'width',
  'size', 'color', 'face',
])

/** Inline style only - a mail client strips a stylesheet and honours the attribute. */
const ALLOWED_STYLES = new Set([
  'color', 'background-color', 'font-size', 'font-family', 'font-weight', 'font-style',
  'text-align', 'text-decoration', 'margin', 'margin-left', 'padding', 'padding-left',
  'line-height', 'border', 'border-collapse', 'vertical-align', 'width',
])

/** Everything else - javascript:, data:, vbscript: - is a link that runs something. */
const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:']

/**
 * Whether an address is one a customer could actually receive mail at.
 *
 * <p>
 * Stricter than the browser's own <code>type="email"</code>, which accepts
 * <code>lim@discochina</code>. That is a valid address inside a company network and a typo
 * everywhere a customer is, so the domain has to carry a dot. The API applies the same rule
 * - a check that only lives in a form is a rule anybody with a HTTP client is exempt from.
 * </p>
 */
export function isEmailAddress(candidate: string): boolean {
  const value = candidate.trim()

  if (value.length === 0 || /\s/.test(value)) return false

  const at = value.lastIndexOf('@')
  if (at <= 0 || at === value.length - 1) return false

  const local = value.slice(0, at)
  const host = value.slice(at + 1)

  if (local.includes('@') || host.includes('@')) return false
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.') || host.includes('..')) {
    return false
  }

  return /^[^\s<>()[\]\\,;:"]+$/.test(local) && /^[A-Za-z0-9.-]+$/.test(host)
}

export interface AddressList {
  addresses: string[]
  /** The first thing typed that is not an address, or null when they all are. */
  invalid: string | null
}

/** Reads the semicolon- or comma-separated lists the To and CC fields are typed into. */
export function parseAddressList(text: string | null | undefined): AddressList {
  const addresses: string[] = []

  for (const part of (text ?? '').split(/[;,]/)) {
    const candidate = part.trim()
    if (candidate.length === 0) continue

    if (!isEmailAddress(candidate)) {
      return { addresses: [], invalid: candidate }
    }

    // The same person twice is one mail with an untidy header, and nobody meant the second.
    if (!addresses.some((existing) => existing.toLowerCase() === candidate.toLowerCase())) {
      addresses.push(candidate)
    }
  }

  return { addresses, invalid: null }
}

/**
 * What the editor may store, given what a browser produced.
 *
 * A contenteditable pastes whatever was on the clipboard - Word's own markup, a website's
 * spans and classes, an inline style per character. Anything outside the allow-list is
 * unwrapped rather than deleted, so the words survive and only the markup goes.
 */
export function sanitizeEmailHtml(html: string): string {
  // Parsed into an inert document rather than assigned to a live element. A detached div is
  // still part of this page: an <img onerror> would be handed a chance to run before a
  // single tag had been removed.
  const parsed = new DOMParser().parseFromString(html, 'text/html')

  const clean = (parent: Element) => {
    for (const node of [...parent.childNodes]) {
      if (node.nodeType === Node.TEXT_NODE) continue

      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove()
        continue
      }

      const element = node as Element

      if (DROPPED_WHOLE.has(element.tagName)) {
        element.remove()
        continue
      }

      clean(element)

      if (!ALLOWED_TAGS.has(element.tagName)) {
        // Unwrapped, not deleted: the words inside are what somebody wrote.
        element.replaceWith(...element.childNodes)
        continue
      }

      scrubAttributes(element)
    }
  }

  clean(parsed.body)
  return parsed.body.innerHTML
}

function scrubAttributes(element: Element) {
  for (const attribute of [...element.attributes]) {
    const name = attribute.name.toLowerCase()

    // Takes every on* handler with it, whatever the browser called it.
    if (!ALLOWED_ATTRIBUTES.has(name)) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (name === 'style') {
      const style = scrubStyle(attribute.value)
      if (style) element.setAttribute('style', style)
      else element.removeAttribute('style')
      continue
    }

    if (name === 'href' && !isSafeHref(attribute.value)) {
      element.removeAttribute('href')
    }
  }

  if (element.tagName === 'A' && element.hasAttribute('href')) {
    // A link opened from webmail must not be able to reach back into the page it came from.
    element.setAttribute('rel', 'noopener noreferrer')
    element.setAttribute('target', '_blank')
  }
}

function scrubStyle(value: string): string {
  return value
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => {
      const [property, ...rest] = declaration.split(':')
      const body = rest.join(':').toLowerCase()

      if (!property || rest.length === 0) return false
      if (!ALLOWED_STYLES.has(property.trim().toLowerCase())) return false

      // url() reaches out to a remote host, and a mail client that loads it tells the
      // sender the message was opened. expression() is old IE running script from CSS.
      return !body.includes('url(') && !body.includes('expression(')
    })
    .join('; ')
}

/**
 * A link's address, as far as it can be trusted.
 *
 * The placeholder is allowed through on purpose: the template stores
 * <code>{{QuotationLink}}</code> in the href and the server replaces it when the mail is
 * composed, so stripping it here would take the signing link out of every template the
 * first time somebody saved one.
 */
function isSafeHref(href: string): boolean {
  const value = href.trim()

  if (value.length === 0) return false
  if (/^\{\{\s*[A-Za-z]+\s*\}\}$/.test(value)) return true

  // Relative, and therefore carrying no scheme of its own.
  if (value.startsWith('/') || value.startsWith('#')) return true

  try {
    return ALLOWED_SCHEMES.includes(new URL(value, 'https://placeholder.invalid').protocol)
  } catch {
    return false
  }
}

/** Whether anything was actually written, ignoring the markup around it. */
export function isEmailHtmlEmpty(html: string | null | undefined): boolean {
  return htmlToPlainText(html).length === 0
}
